import { selectXRayAnalytics } from '@mm/state/selectors/botAnalyticsSelector';
import { Empty, Spin } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import React, { ReactNode, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useSelector } from 'react-redux';
import styles from './AnalyticsCharts.module.css';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import pluralize from 'pluralize';
import { extractDomainName } from '../../../../../../../dashboard/pages/XRayOnboarding/WebsiteReportSummary/constants';
import { Circle, HelpText } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { MOBILE_SMALL } from '../../../../../../constants/screenSize';
import {
  XRayAnalyticsType,
  X_RAY_ANALYTICS_FILTER_TYPES,
} from '@mm/state/interfaces/botAnalytics';

const sortByWDay = (wDay: number[], hits: number[]) => {
  const clone = [...wDay];
  const sortedWDay = clone.sort((a, b) => a - b);
  const remappedHits = sortedWDay.map(value => {
    const index = wDay.findIndex(i => i === value);
    return hits[index];
  });

  return {
    wDay: sortedWDay,
    wDayHits: remappedHits,
  };
};

export const XRayContactsPieChart = ({
  type,
  footer,
  withLegend,
  busy,
}: {
  type: XRayAnalyticsType;
  footer?: ReactNode;
  withLegend?: boolean;
  busy?: boolean;
}) => {
  const [showLegend, setShowLegend] = useState(false);
  const xRayAnalytics = useSelector(selectXRayAnalytics);
  const analyticsByType = xRayAnalytics?.[type];
  const isWDay = type === 'wday';
  const total = analyticsByType?.total;
  const { wDay, wDayHits } = sortByWDay(
    analyticsByType?.wday || [],
    analyticsByType?.hits || [],
  );

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_SMALL;
  const size = !isMobile ? 400 : 320;

  const getLabels = () => {
    switch (type) {
      case X_RAY_ANALYTICS_FILTER_TYPES.wday:
        return wDay?.map(dayNum => moment().day(dayNum).format('dddd'));
      case X_RAY_ANALYTICS_FILTER_TYPES.emailDomain:
        return analyticsByType?.emailDomain?.map(
          domain => extractDomainName(domain) || domain,
        );
      case X_RAY_ANALYTICS_FILTER_TYPES.timeOnSite:
        return analyticsByType?.timeOnSite?.map(val => {
          const secInMin = 60;
          const minVal = Number(val.split('-')?.[0]);
          const maxVal = Number(val.split('-')?.[1]);
          if (Number.isNaN(minVal) || Number.isNaN(maxVal)) {
            const onlyDigits = val.match(/\d+/);
            return (
              (onlyDigits && `${Number(onlyDigits) / secInMin}+ mins`) || val
            );
          }
          return minVal < secInMin && maxVal < secInMin
            ? `${minVal} - ${maxVal} sec`
            : minVal < secInMin && maxVal > secInMin
            ? `${minVal} sec - ${maxVal / secInMin} min`
            : `${minVal / secInMin} - ${maxVal / secInMin} mins`;
        });
      case X_RAY_ANALYTICS_FILTER_TYPES.pageViews:
        return analyticsByType?.views?.map(val => `${val} views`);
      case X_RAY_ANALYTICS_FILTER_TYPES.clicks:
        return analyticsByType?.clicks?.map(val => `${val} clicks`);
      case X_RAY_ANALYTICS_FILTER_TYPES.webSessionsCount:
        return analyticsByType?.webSessionsCount?.map(val => `${val} sessions`);
      default:
        return analyticsByType?.[X_RAY_ANALYTICS_FILTER_TYPES[type]];
    }
  };
  const labels = getLabels();
  const hits = isWDay ? wDayHits : analyticsByType?.hits;

  const backgroundColors = [
    '#6CE5E8',
    '#41B8D5',
    '#2D8BBA',
    '#2F5F98',
    '#31356E',
    '#5E3967',
    '#895273',
    '#B97286',
    '#E28385',
    '#FE9273',
    '#FFA95A',
    '#FFD04C',
    '#C6DE27',
    '#58CB1E',
    '#00AD60',
  ];

  const countPercentage = (value: number) => {
    if (total) {
      const percentage = (value * 100) / total;
      const roundTo = Number.isInteger(percentage) ? 0 : 1;
      return percentage.toFixed(roundTo);
    }
    return value;
  };

  const tooltipCallbacks = {
    label: (tooltipItem: Chart.ChartTooltipItem, data: Chart.ChartData) => {
      const { labels, datasets } = data;
      const { index, datasetIndex } = tooltipItem;

      if (typeof index === 'number' && typeof datasetIndex === 'number') {
        const value = datasets?.[datasetIndex]?.data?.[index] as number;
        const label = labels?.[index];
        return `${label}: ${value} ${pluralize('contact', value)}`;
      }
      return tooltipItem.label || '';
    },
  };

  const data = {
    labels,
    datasets: [
      {
        data: hits,
        backgroundColor: backgroundColors,
        hoverOffset: 4,
      },
    ],
  };

  const options: Chart.ChartOptions = {
    legend: {
      display: false,
    },
    layout: {
      padding: 50,
    },
    tooltips: {
      callbacks: tooltipCallbacks,
    },
    responsive: false,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: 'auto',
        color: '#262626',
        align: 'end',
        anchor: 'end',
        textAlign: 'center',
        clamp: true,
        formatter: (value, context) => {
          const label = context?.chart?.data?.labels?.[context.dataIndex];
          return `${label}\n${countPercentage(value)}%`;
        },
        font: {
          size: 12,
          weight: 400,
          family: 'Roboto',
        },
      },
    },
  };

  return (
    <Spin
      tip="Calculating..."
      size="small"
      spinning={busy}
      wrapperClassName={classNames(styles.spin, {
        [styles.busySpin]: busy || !total,
      })}
    >
      {total ? (
        <>
          <Doughnut
            data={data}
            options={options}
            plugins={[ChartDataLabels]}
            height={size}
            width={size}
          />
          {withLegend && (
            <div className={styles.legendContainer}>
              <div
                onClick={() => setShowLegend(!showLegend)}
                className={styles.legendToggle}
              >
                {showLegend ? 'Hide details' : 'Show details'}
              </div>
              {showLegend && (
                <div className={styles.legend}>
                  {labels?.map((label, index) => (
                    <div className={styles.legendItem} key={label}>
                      <Circle color={backgroundColors?.[index]} />
                      <HelpText
                        className={styles.legendLabel}
                        text={`${label}: ${hits?.[index]} (${countPercentage(
                          hits?.[index] || 0,
                        )}%)`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {footer}
        </>
      ) : (
        <Empty
          image={<div className={styles.emptyDoughnut} />}
          description=""
        />
      )}
    </Spin>
  );
};
